// keyrd_console/src/components/PilotOverview.js
import React, { useEffect, useMemo, useState } from "react";
import { getPilotOverview } from "../api/pilotAdmin";
import PilotGovernanceBanner from "./PilotGovernanceBanner";
import PilotStatusPill from "./PilotStatusPill";
import PilotDataSourceBadge from "./PilotDataSourceBadge";

const pageStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
};

const titleStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: "#0f172a",
  margin: 0,
};

const subtitleStyle = {
  color: "#475569",
  fontSize: 14,
  marginTop: 4,
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
};

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const cardLabelStyle = {
  fontSize: 13,
  color: "#475569",
  fontWeight: 600,
};

const cardValueStyle = {
  fontSize: 28,
  fontWeight: 700,
  color: "#0f172a",
};

const sectionStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
};

const sectionTitleStyle = {
  margin: "0 0 12px 0",
  fontSize: 16,
  fontWeight: 700,
  color: "#0f172a",
};

const rowWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const actionRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
};

const buttonStyle = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#f8fafc",
  color: "#0f172a",
  fontWeight: 600,
  cursor: "pointer",
};

const listStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const listItemStyle = {
  padding: 12,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
};

const itemMainStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const itemTitleStyle = {
  fontWeight: 600,
  color: "#0f172a",
};

const itemMetaStyle = {
  fontSize: 13,
  color: "#64748b",
};

const errorStyle = {
  padding: 14,
  borderRadius: 10,
  background: "#fef2f2",
  color: "#991b1b",
  border: "1px solid #fecaca",
};

const loadingStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#334155",
};

function numberValue(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildSummaryCards(data = {}) {
  return [
    { label: "Total Participants", value: numberValue(data.total_participants) },
    { label: "Review Pending", value: numberValue(data.review_pending_count) },
    { label: "Paused", value: numberValue(data.paused_count) },
    { label: "Suppressed", value: numberValue(data.suppressed_count) },
    { label: "Cooldown Active", value: numberValue(data.cooldown_active_count) },
    { label: "Alerts", value: numberValue(data.alert_count) },
  ];
}

function LifecycleCounts({ lifecycleCounts = {} }) {
  const entries = Object.entries(lifecycleCounts || {});
  if (!entries.length) {
    return <div style={itemMetaStyle}>No lifecycle counts available.</div>;
  }

  return (
    <div style={rowWrapStyle}>
      {entries.map(([key, value]) => (
        <PilotStatusPill
          key={key}
          status={key}
          label={`${key}: ${numberValue(value)}`}
        />
      ))}
    </div>
  );
}

function RecentActions({ items = [] }) {
  if (!items.length) {
    return <div style={itemMetaStyle}>No recent operator actions available.</div>;
  }

  return (
    <div style={listStyle}>
      {items.map((item, idx) => (
        <div key={`${item.timestamp_utc || "na"}-${idx}`} style={listItemStyle}>
          <div style={itemMainStyle}>
            <div style={itemTitleStyle}>
              {item.event_type || "operator_action"}
            </div>
            <div style={itemMetaStyle}>
              Operator: {item.operator_id || "unknown"} · Participant:{" "}
              {item.participant_uuid || "n/a"}
            </div>
            <div style={itemMetaStyle}>
              {item.reason_code || "no reason supplied"}
            </div>
          </div>

          <div style={itemMainStyle}>
            <PilotStatusPill status={item.severity || "unknown"} label={item.severity || "unknown"} />
            <div style={itemMetaStyle}>{item.timestamp_utc || "unknown time"}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PilotOverview({ auth, onOpenParticipants, onOpenQueue }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await getPilotOverview(auth);
        if (!cancelled) {
          setPayload(res?.data || res || {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load pilot overview.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const data = useMemo(() => payload || {}, [payload]);
  const summaryCards = useMemo(() => buildSummaryCards(data), [data]);

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingStyle}>Loading pilot overview…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={titleRowStyle}>
        <div>
          <h1 style={titleStyle}>Pilot Overview</h1>
          <div style={subtitleStyle}>
            Durable command surface for Stages 0–3. Review-only posture remains in force.
          </div>
        </div>
        <PilotDataSourceBadge
          source={data.data_source}
          stub={Boolean(data.stub)}
        />
      </div>

      <PilotGovernanceBanner auth={auth} system={data.system || data} />

      <div style={gridStyle}>
        {summaryCards.map((card) => (
          <div key={card.label} style={cardStyle}>
            <div style={cardLabelStyle}>{card.label}</div>
            <div style={cardValueStyle}>{card.value}</div>
          </div>
        ))}
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Lifecycle Counts</h2>
        <LifecycleCounts lifecycleCounts={data.lifecycle_counts} />
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Pilot Mode & Safety Posture</h2>
        <div style={rowWrapStyle}>
          <PilotStatusPill
            status={data.pilot_mode || auth?.pilotMode || "unknown"}
            label={`Mode: ${data.pilot_mode || auth?.pilotMode || "unknown"}`}
          />
          <PilotStatusPill
            status={(data.shadow_only_mode ?? true) ? "true" : "false"}
            label={(data.shadow_only_mode ?? true) ? "Shadow-Only" : "Shadow Disabled"}
          />
          <PilotStatusPill
            status={(data.rl_updates_blocked ?? true) ? "true" : "false"}
            label={(data.rl_updates_blocked ?? true) ? "RL Updates Blocked" : "RL Updates Enabled"}
          />
          <PilotStatusPill
            status={(data.live_issuance_enabled ?? false) ? "true" : "false"}
            label={(data.live_issuance_enabled ?? false) ? "Live Issuance Enabled" : "Live Issuance Disabled"}
          />
          <PilotStatusPill
            status={(data.system_pause ?? false) ? "paused" : "active"}
            label={(data.system_pause ?? false) ? "System Paused" : "System Running"}
          />
        </div>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Next Actions</h2>
        <div style={actionRowStyle}>
          <button style={buttonStyle} onClick={onOpenParticipants}>
            Open Participants
          </button>
          <button style={buttonStyle} onClick={onOpenQueue}>
            Open Operations Queue
          </button>
        </div>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Recent Operator Actions</h2>
        <RecentActions items={data.recent_operator_actions || []} />
      </div>
    </div>
  );
}

export default PilotOverview;